/**
 * Invite API — both the invitee-facing side of the invite flow (preview,
 * the two accept paths) and the admin-facing side used by the Team
 * Management page (create/list/revoke/resend).
 */
import api from './api.service';
import type { AuthResult, AuthUser, AuthTokens } from './auth.api';

export interface InvitePreview {
  email: string;
  tenantName: string;
  role: string;
  hasExistingAccount: boolean;
}

export async function previewInvite(token: string): Promise<InvitePreview> {
  const res = await api.get<{ success: boolean; data: InvitePreview }>(
    `/invites/${encodeURIComponent(token)}`
  );
  return res.data;
}

export interface AcceptInviteNewIdentityPayload {
  password: string;
  firstName: string;
  lastName: string;
}

/** Brand-new identity only — the backend hard-rejects this if the email
 *  already has an account (see auth security invariant from Phase 2). */
export async function acceptInviteNewIdentity(
  token: string,
  payload: AcceptInviteNewIdentityPayload
): Promise<AuthResult> {
  const res = await api.post<{ success: boolean; data: AuthResult }>(
    `/invites/${encodeURIComponent(token)}/accept`,
    payload
  );
  return res.data;
}

export interface AcceptInviteExistingResult {
  message: string;
  // The accepting identity's session was minted tenant-less at login — the
  // backend mints a fresh {user, tenant, tokens} triple here (same shape as
  // AuthResult) so the client can adopt the new tenant/role/permissions
  // immediately instead of carrying a stale, tenant-less session forward.
  user: AuthUser;
  tenant: { id: string; name: string; subdomain: string };
  tokens: AuthTokens;
}

/** Requires the caller to already be authenticated as the invited email —
 *  api.service's request interceptor attaches the bearer token automatically. */
export async function acceptInviteExisting(token: string): Promise<AcceptInviteExistingResult> {
  const res = await api.post<{ success: boolean; data: AcceptInviteExistingResult }>(
    `/invites/${encodeURIComponent(token)}/accept-existing`
  );
  return res.data;
}

// ---- Admin-facing: Team Management page ----

export interface AdminInvite {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt: string;
  createdAt: string;
}

export interface CreateInvitePayload {
  email: string;
  role?: string;
  roleId?: string;
  reportingTo?: string;
}

/** Fire-and-forget on the backend's side — invite.service.ts enqueues the
 *  email onto Bull and returns immediately, so this call is only ever as
 *  slow as the DB write, never the email send. */
export async function createInvite(payload: CreateInvitePayload): Promise<AdminInvite> {
  const res = await api.post<{ success: boolean; data: { message: string; invite: AdminInvite } }>(
    '/invites',
    payload
  );
  return res.data.invite;
}

export async function listInvites(status?: AdminInvite['status']): Promise<AdminInvite[]> {
  const res = await api.get<{ success: boolean; data: { invites: AdminInvite[] } }>('/invites', {
    params: status ? { status } : undefined,
  });
  return res.data.invites;
}

export async function revokeInvite(id: string): Promise<void> {
  await api.post(`/invites/${encodeURIComponent(id)}/revoke`);
}

export async function resendInvite(id: string): Promise<void> {
  await api.post(`/invites/${encodeURIComponent(id)}/resend`);
}
